import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environment';
import {
  HotspotPassenger,
  HotspotPassengerBulkSyncResponse,
  HotspotPassengerSyncResult,
  HotspotSessionRelinkResponse,
  HotspotSessionRemapResponse,
  HotspotSessionSyncError,
  HotspotSessionSyncAllResponse,
  HotspotSessionSyncResponse,
  HotspotSyncRun,
  HotspotSyncRunsResponse
} from '../models/hotspot-passenger.model';

@Injectable({
  providedIn: 'root'
})
export class HotspotPassengerService {
  private apiUrl = `${environment.apiUrl}/hotspot/passengers`;
  private compatibilityApiUrl = `${environment.apiUrl}/powerlynx/passengers`;
  private hotspotApiUrl = `${environment.apiUrl}/hotspot`;
  private powerlynxApiUrl = `${environment.apiUrl}/powerlynx`;

  constructor(private http: HttpClient) {}

  getHotspotPassengers(page = 1, perPage = 100, search = ''): Observable<HotspotPassenger[]> {
    let params = new HttpParams()
      .set('page', page)
      .set('perPage', perPage);

    if (search.trim()) {
      params = params.set('search', search.trim());
    }

    return this.http.get<unknown>(this.apiUrl, { params }).pipe(
      map(response => this.extractPassengerArray(response).map(row => this.mapHotspotPassenger(row)))
    );
  }

  getHotspotPassenger(id: string): Observable<HotspotPassenger | undefined> {
    return this.http.get<unknown>(`${this.apiUrl}/${id}`).pipe(
      map(response => {
        const body = this.unwrapApiValue(response);
        const row = this.pickValue(body, ['data', 'Data']) || body;
        return row ? this.mapHotspotPassenger(row) : undefined;
      })
    );
  }

  syncPassengerProfile(id: string): Observable<HotspotPassengerSyncResult> {
    return this.http.post<unknown>(`${this.apiUrl}/${id}/sync-profile`, {}).pipe(
      catchError(error => {
        if (error?.status === 404) {
          return this.http.post<unknown>(`${this.compatibilityApiUrl}/${id}/sync-profile`, {});
        }

        return throwError(() => error);
      }),
      map(response => this.mapPassengerSyncResult(response))
    );
  }

  syncPassengerProfiles(query: {
    page: number;
    perPage: number;
    search?: string;
    sortBy?: string;
    sortDirection?: string;
  }): Observable<HotspotPassengerBulkSyncResponse> {
    let params = new HttpParams()
      .set('page', query.page)
      .set('perPage', query.perPage)
      .set('sortDirection', query.sortDirection || 'desc');

    if (query.search?.trim()) {
      params = params.set('search', query.search.trim());
    }

    if (query.sortBy?.trim()) {
      params = params.set('sortBy', query.sortBy.trim());
    }

    return this.http.post<unknown>(`${this.apiUrl}/sync-profiles`, {}, { params }).pipe(
      catchError(error => {
        if (error?.status === 404) {
          return this.http.post<unknown>(`${this.compatibilityApiUrl}/sync-profiles`, {}, { params });
        }

        return throwError(() => error);
      }),
      map(response => this.mapPassengerBulkSyncResponse(response))
    );
  }

  syncSessions(limit: number, offset: number): Observable<HotspotSessionSyncResponse> {
    const params = new HttpParams()
      .set('limit', limit)
      .set('offset', offset);

    return this.http.post<unknown>(`${this.hotspotApiUrl}/sessions/sync`, {}, { params }).pipe(
      catchError(error => {
        if (error?.status === 404) {
          return this.http.post<unknown>(`${this.powerlynxApiUrl}/sessions/sync`, {}, { params });
        }

        return throwError(() => error);
      }),
      map(response => this.mapSessionSyncResponse(response))
    );
  }

  syncAllSessions(limit: number, startOffset: number, maxBatches?: number | null): Observable<HotspotSessionSyncAllResponse> {
    let params = new HttpParams()
      .set('limit', limit)
      .set('startOffset', startOffset);

    if (maxBatches !== null && maxBatches !== undefined && Number(maxBatches) > 0) {
      params = params.set('maxBatches', Number(maxBatches));
    }

    return this.http.post<unknown>(`${this.hotspotApiUrl}/sessions/sync-all`, {}, { params }).pipe(
      catchError(error => {
        if (error?.status === 404) {
          return this.http.post<unknown>(`${this.powerlynxApiUrl}/sessions/sync-all`, {}, { params });
        }

        return throwError(() => error);
      }),
      map(response => this.mapSessionSyncAllResponse(response))
    );
  }

  remapExistingSessions(limit: number): Observable<HotspotSessionRemapResponse> {
    const params = new HttpParams().set('limit', limit);

    return this.http.post<unknown>(`${this.hotspotApiUrl}/sessions/remap-existing`, {}, { params }).pipe(
      catchError(error => {
        if (error?.status === 404) {
          return this.http.post<unknown>(`${this.powerlynxApiUrl}/sessions/remap-existing`, {}, { params });
        }

        return throwError(() => error);
      }),
      map(response => this.mapSessionRemapResponse(response))
    );
  }

  relinkPassengerProfiles(limit: number): Observable<HotspotSessionRelinkResponse> {
    const params = new HttpParams().set('limit', limit);

    return this.http.post<unknown>(`${this.hotspotApiUrl}/sessions/relink-passenger-profiles`, {}, { params }).pipe(
      catchError(error => {
        if (error?.status === 404) {
          return this.http.post<unknown>(`${this.powerlynxApiUrl}/sessions/relink-passenger-profiles`, {}, { params });
        }

        return throwError(() => error);
      }),
      map(response => this.mapSessionRelinkResponse(response))
    );
  }

  getSyncRuns(resource?: 'Passengers' | 'Sessions', page = 1, perPage = 25): Observable<HotspotSyncRunsResponse> {
    let params = new HttpParams()
      .set('page', page)
      .set('perPage', perPage);

    if (resource) {
      params = params.set('resource', resource);
    }

    return this.http.get<unknown>(`${this.hotspotApiUrl}/sync-runs`, { params }).pipe(
      map(response => this.mapSyncRunsResponse(response, page, perPage))
    );
  }

  getSessionSyncRuns(page = 1, perPage = 25): Observable<HotspotSyncRunsResponse> {
    return this.getSyncRuns('Sessions', page, perPage);
  }

  private mapSyncRunsResponse(response: unknown, page: number, perPage: number): HotspotSyncRunsResponse {
    const body = this.unwrapApiValue(response);
    const data = this.extractSyncRunArray(body);
    const meta = this.unwrapApiValue(this.pickValue(body, ['meta', 'Meta'])) || {};

    return {
      data: data.map(row => this.mapSyncRun(row)),
      meta: {
        page: this.toNumber(this.pickValue(meta, ['page', 'Page']), page),
        perPage: this.toNumber(this.pickValue(meta, ['perPage', 'PerPage', 'pageSize', 'PageSize']), perPage),
        total: this.toNumber(this.pickValue(meta, ['total', 'Total', 'totalCount', 'TotalCount']), data.length),
        totalPages: this.toNumber(this.pickValue(meta, ['totalPages', 'TotalPages']), 1)
      }
    };
  }

  private extractPassengerArray(value: any): any[] {
    const unwrapped = this.unwrapApiValue(value);
    if (Array.isArray(unwrapped)) {
      return unwrapped;
    }

    if (!unwrapped || typeof unwrapped !== 'object') {
      return [];
    }

    for (const key of ['data', 'Data', 'items', 'Items', 'results', 'Results']) {
      const candidate = this.unwrapApiValue(unwrapped[key]);
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }

    return [];
  }

  private mapHotspotPassenger(row: any): HotspotPassenger {
    const id = this.pickValue(row, ['id', 'ID']);
    const name = this.pickValue(row, ['displayName', 'name', 'Name']);
    const labels = this.unwrapApiValue(this.pickValue(row, ['labels', 'Labels'])) || [];

    return {
      id: String(id),
      provider: this.normalizeProvider(this.pickValue(row, ['provider', 'Provider'])),
      externalId: String(id || this.pickValue(row, ['externalId', 'externalID', 'ExternalID']) || ''),
      displayName: String(name || `Hotspot passenger ${id || ''}`).trim(),
      phone: this.pickValue(row, ['phone', 'Phone']),
      email: this.pickValue(row, ['email', 'Email']),
      status: this.normalizePassengerStatus(this.pickValue(row, ['status', 'Status'])),
      onlineState: this.pickValue(row, ['isOnline', 'IsOnline']) === true ? 'Online' : 'Offline',
      firstSeen: this.pickValue(row, ['firstSeen', 'createdAt', 'CreatedAt']) || '',
      lastSeen: this.pickValue(row, ['lastSeen', 'updatedAt', 'UpdatedAt', 'lastOnline', 'LastOnline']),
      lastOnline: this.pickValue(row, ['lastOnline', 'LastOnline']),
      lastSynced: this.pickValue(row, ['lastSynced', 'LastSynced', 'lastSyncedAt', 'LastSyncedAt']),
      packageName: Array.isArray(labels)
        ? labels.map(label => this.pickValue(label, ['title', 'Title'])).filter(Boolean).join(', ')
        : undefined,
      profileId: this.pickValue(row, ['profileId', 'profileID', 'passengerProfileID', 'PassengerProfileID'])
    };
  }

  private mapPassengerSyncResult(response: any): HotspotPassengerSyncResult {
    const body = this.unwrapApiValue(response);
    const profile = this.pickValue(body, ['profile', 'Profile']) || {};
    const externalIdentity = this.pickValue(body, ['externalIdentity', 'ExternalIdentity']) || {};

    return {
      profile: {
        id: String(this.pickValue(profile, ['id', 'profileId', 'passengerProfileID', 'PassengerProfileID']) || ''),
        displayName: this.pickValue(profile, ['displayName', 'DisplayName']) || 'Unknown passenger',
        firstName: this.pickValue(profile, ['firstName', 'FirstName']),
        lastName: this.pickValue(profile, ['lastName', 'LastName']),
        phone: this.pickValue(profile, ['phone', 'Phone']),
        email: this.pickValue(profile, ['email', 'Email']),
        primarySource: this.pickValue(profile, ['primarySource', 'PrimarySource']) || 'Unknown',
        linkedAppUser: this.pickValue(profile, ['linkedAppUser', 'LinkedAppUser']),
        firstSeen: this.pickValue(profile, ['firstSeen', 'firstSeenAt', 'FirstSeenAt', 'createdAt', 'CreatedAt']) || '',
        lastSeen: this.pickValue(profile, ['lastSeen', 'lastSeenAt', 'LastSeenAt']),
        updatedDate: this.pickValue(profile, ['updatedDate', 'updatedAt', 'UpdatedAt']) || '',
        externalIdentities: [],
        notes: this.pickValue(profile, ['notes', 'Notes'])
      },
      externalIdentity: {
        id: String(this.pickValue(externalIdentity, ['id', 'passengerExternalIdentityID', 'PassengerExternalIdentityID']) || ''),
        provider: this.pickValue(externalIdentity, ['provider', 'Provider']) || 'Powerlynx',
        entityType: this.pickValue(externalIdentity, ['entityType', 'EntityType']) || 'Customer',
        externalId: String(this.pickValue(externalIdentity, ['externalId', 'externalID', 'ExternalID']) || ''),
        status: this.pickValue(externalIdentity, ['status', 'Status']) || 'Unknown',
        onlineState: this.pickValue(externalIdentity, ['onlineState', 'OnlineState'])
          || (this.pickValue(externalIdentity, ['isOnline', 'IsOnline']) === true ? 'Online' : 'Unknown'),
        lastOnline: this.pickValue(externalIdentity, ['lastOnline', 'LastOnline']),
        firstSynced: this.pickValue(externalIdentity, ['firstSynced', 'firstSyncedAt', 'FirstSyncedAt']),
        lastSynced: this.pickValue(externalIdentity, ['lastSynced', 'lastSyncedAt', 'LastSyncedAt']),
        phone: this.pickValue(externalIdentity, ['phone', 'Phone']),
        email: this.pickValue(externalIdentity, ['email', 'Email'])
      },
      createdProfile: Boolean(this.pickValue(body, ['createdProfile', 'CreatedProfile'])),
      createdExternalIdentity: Boolean(this.pickValue(body, ['createdExternalIdentity', 'CreatedExternalIdentity']))
    };
  }

  private mapPassengerBulkSyncResponse(response: any): HotspotPassengerBulkSyncResponse {
    const body = this.unwrapResponseData(response);
    return {
      hotspotSyncRunID: this.pickValue(body, ['hotspotSyncRunID', 'HotspotSyncRunID']),
      requestedPage: this.toNumber(this.pickValue(body, ['requestedPage', 'RequestedPage', 'page', 'Page']), 1),
      requestedPerPage: this.toNumber(this.pickValue(body, ['requestedPerPage', 'RequestedPerPage', 'perPage', 'PerPage']), 100),
      processed: this.toNumber(this.pickValue(body, ['processed', 'Processed']), 0),
      createdProfiles: this.toNumber(this.pickValue(body, ['createdProfiles', 'CreatedProfiles', 'createdPassengerProfiles', 'CreatedPassengerProfiles']), 0),
      createdExternalIdentities: this.toNumber(this.pickValue(body, ['createdExternalIdentities', 'CreatedExternalIdentities']), 0),
      errors: this.mapErrorArray(this.pickValue(body, ['errors', 'Errors', 'errorDetails', 'ErrorDetails']))
    };
  }

  private mapSessionSyncResponse(response: any): HotspotSessionSyncResponse {
    const body = this.unwrapResponseData(response);
    return {
      hotspotSyncRunID: this.pickValue(body, ['hotspotSyncRunID', 'HotspotSyncRunID']) || '',
      requestedLimit: this.toNumber(this.pickValue(body, ['requestedLimit', 'RequestedLimit']), 0),
      requestedOffset: this.toNumber(this.pickValue(body, ['requestedOffset', 'RequestedOffset']), 0),
      processed: this.toNumber(this.pickValue(body, ['processed', 'Processed']), 0),
      createdSessions: this.toNumber(this.pickValue(body, ['createdSessions', 'CreatedSessions']), 0),
      createdAssetMappings: this.toNumber(this.pickValue(body, ['createdAssetMappings', 'CreatedAssetMappings']), 0),
      results: this.extractArray(this.pickValue(body, ['results', 'Results'])).map(row => this.mapSessionSyncResultRow(row)),
      errors: this.mapErrorArray(this.pickValue(body, ['errors', 'Errors', 'errorDetails', 'ErrorDetails']))
    };
  }

  private mapSessionSyncAllResponse(response: any): HotspotSessionSyncAllResponse {
    const body = this.unwrapResponseData(response);
    return {
      hotspotSyncRunID: this.pickValue(body, ['hotspotSyncRunID', 'HotspotSyncRunID']),
      requestedLimit: this.toNumber(this.pickValue(body, ['requestedLimit', 'RequestedLimit', 'limit', 'Limit']), 0),
      startOffset: this.toNumber(this.pickValue(body, ['startOffset', 'StartOffset', 'requestedOffset', 'RequestedOffset']), 0),
      nextOffset: this.toOptionalNumber(this.pickValue(body, ['nextOffset', 'NextOffset'])),
      maxBatches: this.toOptionalNumber(this.pickValue(body, ['maxBatches', 'MaxBatches'])),
      batchesProcessed: this.toNumber(this.pickValue(body, ['batchesProcessed', 'BatchesProcessed']), 0),
      processed: this.toNumber(this.pickValue(body, ['processed', 'Processed']), 0),
      createdSessions: this.toNumber(this.pickValue(body, ['createdSessions', 'CreatedSessions']), 0),
      createdAssetMappings: this.toNumber(this.pickValue(body, ['createdAssetMappings', 'CreatedAssetMappings']), 0),
      errors: this.mapErrorArray(this.pickValue(body, ['errors', 'Errors', 'errorDetails', 'ErrorDetails']))
    };
  }

  private mapSessionRemapResponse(response: any): HotspotSessionRemapResponse {
    const body = this.unwrapResponseData(response);
    return {
      requestedLimit: this.toNumber(this.pickValue(body, ['requestedLimit', 'RequestedLimit']), 0),
      reviewed: this.toNumber(this.pickValue(body, ['reviewed', 'Reviewed']), 0),
      remappedSessions: this.toNumber(this.pickValue(body, ['remappedSessions', 'RemappedSessions']), 0),
      createdAssetMappings: this.toNumber(this.pickValue(body, ['createdAssetMappings', 'CreatedAssetMappings']), 0),
      errors: this.toNumber(this.pickValue(body, ['errors', 'Errors']), 0),
      errorDetails: this.mapErrorArray(this.pickValue(body, ['errorDetails', 'ErrorDetails']))
    };
  }

  private mapSessionRelinkResponse(response: any): HotspotSessionRelinkResponse {
    const body = this.unwrapResponseData(response);
    return {
      requestedLimit: this.toNumber(this.pickValue(body, ['requestedLimit', 'RequestedLimit']), 0),
      reviewed: this.toNumber(this.pickValue(body, ['reviewed', 'Reviewed']), 0),
      relinkedSessions: this.toNumber(this.pickValue(body, ['relinkedSessions', 'RelinkedSessions']), 0),
      alreadyLinked: this.toNumber(this.pickValue(body, ['alreadyLinked', 'AlreadyLinked']), 0),
      unmatched: this.toNumber(this.pickValue(body, ['unmatched', 'Unmatched']), 0),
      errors: this.toNumber(this.pickValue(body, ['errors', 'Errors']), 0),
      errorDetails: this.mapErrorArray(this.pickValue(body, ['errorDetails', 'ErrorDetails']))
    };
  }

  private mapSessionSyncResultRow(row: any): any {
    return {
      externalID: String(this.pickValue(row, ['externalID', 'externalId', 'ExternalID']) || ''),
      hotspotSessionRecordID: this.pickValue(row, ['hotspotSessionRecordID', 'HotspotSessionRecordID']),
      passengerProfileID: this.pickValue(row, ['passengerProfileID', 'PassengerProfileID']),
      hotspotAssetMappingID: this.pickValue(row, ['hotspotAssetMappingID', 'HotspotAssetMappingID']),
      organisationID: this.pickValue(row, ['organisationID', 'organisationId', 'OrganisationID']),
      vehicleID: this.pickValue(row, ['vehicleID', 'vehicleId', 'VehicleID']),
      nasIdentifier: this.pickValue(row, ['nasIdentifier', 'NasIdentifier']),
      createdSession: Boolean(this.pickValue(row, ['createdSession', 'CreatedSession'])),
      createdAssetMapping: Boolean(this.pickValue(row, ['createdAssetMapping', 'CreatedAssetMapping']))
    };
  }

  private mapErrorArray(value: any): HotspotSessionSyncError[] {
    return this.extractArray(value).map((row: any) => {
      if (typeof row === 'string') {
        return { message: row };
      }

      return {
        externalID: this.pickValue(row, ['externalID', 'externalId', 'ExternalID']) || this.pickValue(row, ['id', 'ID']),
        message: this.pickValue(row, ['message', 'Message', 'error', 'Error']),
        detail: this.pickValue(row, ['detail', 'Detail', 'errorMessage', 'ErrorMessage'])
      };
    });
  }

  private extractSyncRunArray(value: any): any[] {
    const unwrapped = this.unwrapApiValue(value);
    if (Array.isArray(unwrapped)) {
      return unwrapped;
    }

    if (!unwrapped || typeof unwrapped !== 'object') {
      return [];
    }

    for (const key of ['data', 'Data', 'items', 'Items', 'results', 'Results']) {
      const candidate = this.unwrapApiValue(unwrapped[key]);
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }

    return [];
  }

  private mapSyncRun(row: any): HotspotSyncRun {
    return {
      hotspotSyncRunID: this.pickValue(row, ['hotspotSyncRunID', 'hotspotSyncRunId', 'HotspotSyncRunID', 'id', 'ID']),
      id: this.pickValue(row, ['id', 'ID']),
      provider: this.pickValue(row, ['provider', 'Provider']),
      resource: this.pickValue(row, ['resource', 'Resource']),
      syncType: this.pickValue(row, ['syncType', 'SyncType']),
      status: this.pickValue(row, ['status', 'Status']),
      startedAt: this.pickValue(row, ['startedAt', 'StartedAt']),
      completedAt: this.pickValue(row, ['completedAt', 'CompletedAt']),
      createdAt: this.pickValue(row, ['createdAt', 'CreatedAt']),
      page: this.toOptionalNumber(this.pickValue(row, ['page', 'Page'])),
      perPage: this.toOptionalNumber(this.pickValue(row, ['perPage', 'PerPage'])),
      requested: this.toOptionalNumber(this.pickValue(row, ['requested', 'Requested'])),
      requestedLimit: this.toOptionalNumber(this.pickValue(row, ['requestedLimit', 'RequestedLimit'])),
      requestedOffset: this.toOptionalNumber(this.pickValue(row, ['requestedOffset', 'RequestedOffset'])),
      processed: this.toOptionalNumber(this.pickValue(row, ['processed', 'Processed'])),
      createdProfiles: this.toOptionalNumber(this.pickValue(row, ['createdProfiles', 'CreatedProfiles'])),
      createdPassengerProfiles: this.toOptionalNumber(this.pickValue(row, ['createdPassengerProfiles', 'CreatedPassengerProfiles'])),
      createdExternalIdentities: this.toOptionalNumber(this.pickValue(row, ['createdExternalIdentities', 'CreatedExternalIdentities'])),
      createdSessions: this.toOptionalNumber(this.pickValue(row, ['createdSessions', 'CreatedSessions'])),
      createdAssetMappings: this.toOptionalNumber(this.pickValue(row, ['createdAssetMappings', 'CreatedAssetMappings'])),
      errorCount: this.toOptionalNumber(this.pickValue(row, ['errorCount', 'ErrorCount'])),
      errors: this.toOptionalNumber(this.pickValue(row, ['errors', 'Errors'])),
      errorMessage: this.pickValue(row, ['errorMessage', 'ErrorMessage'])
    };
  }

  private unwrapApiValue(value: any): any {
    if (value?.$values) {
      return value.$values;
    }

    return value;
  }

  private unwrapResponseData(value: any): any {
    const body = this.unwrapApiValue(value);
    return this.pickValue(body, ['data', 'Data']) || body || {};
  }

  private extractArray(value: any): any[] {
    const unwrapped = this.unwrapApiValue(value);
    if (Array.isArray(unwrapped)) {
      return unwrapped;
    }

    if (Array.isArray(unwrapped?.$values)) {
      return unwrapped.$values;
    }

    return [];
  }

  private pickValue(source: any, keys: string[]): any {
    if (!source || typeof source !== 'object') {
      return undefined;
    }

    for (const key of keys) {
      const value = source[key];
      if (value !== undefined && value !== null) {
        return value;
      }
    }

    return undefined;
  }

  private toNumber(value: any, fallback: number): number {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : fallback;
  }

  private toOptionalNumber(value: any): number | undefined {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : undefined;
  }

  private normalizeProvider(value: any): 'Powerlynx' | 'Hotspot' {
    return String(value || '').toLowerCase() === 'powerlynx' ? 'Powerlynx' : 'Hotspot';
  }

  private normalizePassengerStatus(value: any): HotspotPassenger['status'] {
    const status = String(value || '').toLowerCase();
    if (status === 'active') {
      return 'Active';
    }

    if (status === 'suspended') {
      return 'Suspended';
    }

    if (status === 'expired') {
      return 'Expired';
    }

    return 'Unknown';
  }
}
